import bbox from '@turf/bbox'
import Protobuffer from 'pbf'
import { VectorTile } from '@mapbox/vector-tile'
import booleanIntersects from '@turf/boolean-intersects'
import { FeatureCollection } from '@turf/helpers'
import { BBox, Polygon, Position } from 'src/common-services/types/geojson-types'
import { Bounds, Tile } from 'src/types/map'
import { MicroVenue } from 'src/common-services/types/venues-types'

function degToRad(deg: number): number {
  return (deg * Math.PI) / 180
}

function lonToTileX(lon: number, zoom: number): number {
  return Math.floor(((lon + 180) / 360) * Math.pow(2, zoom))
}

function latToTileY(lat: number, zoom: number): number {
  return Math.floor(
    ((1 - Math.log(Math.tan(degToRad(lat)) + 1 / Math.cos(degToRad(lat))) / Math.PI) / 2) * Math.pow(2, zoom)
  )
}

function tileIndexToLong(x: number, z: number) {
  return (x / Math.pow(2, z)) * 360 - 180
}

function tileIndexToLat(y: number, z: number) {
  const n = Math.PI - (2 * Math.PI * y) / Math.pow(2, z)
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)))
}

export function pointIsInBounds(bounds: Bounds, point: Position): boolean {
  const [x, y] = point
  let isLongInRange = false
  // in case longitude 180 is inside the box (looking at you aleutian islands)
  if (bounds.northEast[0] < bounds.southWest[0]) {
    isLongInRange = x >= bounds.southWest[0] || x <= bounds.northEast[0]
  } else {
    isLongInRange = x >= bounds.southWest[0] && x <= bounds.northEast[0]
  }
  return y >= bounds.southWest[1] && y <= bounds.northEast[1] && isLongInRange
}

function getMinMaxTileIndexByBoundary({
  minLat,
  maxLat,
  minLong,
  maxLong,
  zoom,
}: {
  minLong: number
  maxLong: number
  minLat: number
  maxLat: number
  zoom: number
}) {
  const minX = lonToTileX(minLong, zoom)
  const maxX = lonToTileX(maxLong, zoom)
  const minY = latToTileY(maxLat, zoom)
  const maxY = latToTileY(minLat, zoom)
  return { minX, maxX, minY, maxY }
}

function getTileBoundaryByTileIndex({ x, y, z }: Tile) {
  const swLong = tileIndexToLong(x, z)
  const swLat = tileIndexToLat(y + 1, z)
  const neLong = tileIndexToLong(x + 1, z)
  const neLat = tileIndexToLat(y, z)
  return { southWest: [swLong, swLat], northEast: [neLong, neLat] }
}

export function getTilesForBounds(currentBounds: Bounds, zoom: number): Tile[] {
  const { minX, maxX, minY, maxY } = getMinMaxTileIndexByBoundary({
    minLat: currentBounds.southWest[1],
    minLong: currentBounds.southWest[0],
    maxLat: currentBounds.northEast[1],
    maxLong: currentBounds.northEast[0],
    zoom,
  })
  const tiles = []
  for (let x = minX; x <= maxX; x++) {
    for (let y = minY; y <= maxY; y++) {
      tiles.push({ x, y, z: zoom })
    }
  }
  return tiles
}

export function getTilesToCover(polygon: Polygon): Tile[] {
  const box = bbox(polygon)
  const bounds: Bounds = {
    northEast: [box[2], box[3]],
    southWest: [box[0], box[1]],
    center: [0, 0],
  }

  const tiles = getTilesForBounds(bounds, 12)
  const featureCollection = convertTilesToFeatureCollection(tiles)
  const filteredFeatures = featureCollection.features.filter((feature) => {
    return booleanIntersects(feature, polygon)
  })
  return filteredFeatures.map((x) => ({ x: x.properties.x, y: x.properties.y, z: x.properties.zoom }))
}

type TileFeatureProps = { x: number; y: number; zoom: number }

export function getPolygonFromTile(tile: Tile): Polygon {
  const { southWest, northEast } = getTileBoundaryByTileIndex(tile)
  return {
    type: 'Polygon',
    coordinates: [[southWest, [northEast[0], southWest[1]], northEast, [southWest[0], northEast[1]], southWest]],
  }
}

export function convertTilesToFeatureCollection(tileCoords: Tile[]): FeatureCollection<Polygon, TileFeatureProps> {
  // Convert the tile boundaries to geojson.
  return {
    type: 'FeatureCollection',
    features: tileCoords.map(({ x, y, z: zoom }) => {
      return {
        type: 'Feature',
        properties: { x, y, zoom },
        geometry: getPolygonFromTile({ x, y, z: zoom }),
      }
    }),
  }
}

function bboxToBounds(bbox: BBox): Bounds {
  return {
    northEast: [bbox[2], bbox[3]],
    southWest: [bbox[0], bbox[1]],
    center: [0, 0],
  }
}

export function isWithinBBox(bbox: BBox, tile: Tile) {
  const tileCoords = getPolygonFromTile(tile).coordinates[0]
  const bounds = bboxToBounds(bbox)
  return tileCoords.find((x) => pointIsInBounds(bounds, x)) !== undefined
}

export const LOCATION_TILE_LAYER_NAME = 'poi-layer'
export const PARCEL_TILE_LAYER_NAME = 'parcel-layer'
export const DEMO_TILE_LAYER_NAME = 'demo-block-layer'
export const DEMO_GROUP_TILE_LAYER_NAME = 'demo-group-layer'

export function parseLocationTile(x: number, y: number, z: number, buffer: ArrayBuffer): MicroVenue[] {
  const tile = new VectorTile(new Protobuffer(buffer))
  const layer = tile.layers[LOCATION_TILE_LAYER_NAME]
  // if we load a tile in the middle of nowhere, it will not have a layer
  if (!layer) {
    return []
  }
  const length = layer.length
  const result: MicroVenue[] = []
  for (let i = 0; i < length; i++) {
    const feature = layer.feature(i)
    const geom = feature.toGeoJSON(x, y, z) as any
    const mapped = {
      ...feature.properties,
      point: geom.geometry,
    }
    result.push(mapped as MicroVenue)
  }
  return result
}

export function parseParceTile(x: number, y: number, z: number, buffer: ArrayBuffer) {
  const tile = new VectorTile(new Protobuffer(buffer))
  const layer = tile.layers[PARCEL_TILE_LAYER_NAME]
  // if we load a tile in the middle of nowhere, it will not have a layer
  if (!layer) {
    return []
  }
  const length = layer.length
  const result = []
  for (let i = 0; i < length; i++) {
    const feature = layer.feature(i)
    const geom = feature.toGeoJSON(x, y, z)
    result.push(geom)
  }
  return result
}
